// 剣舞の借りている絵を専用の絵へ (2026-11-01)。記録 = docs/research/2026-11-01-blade-own-art.md
//   型の1枚目: 跳んで叩く (δ) の2発目「剣を抜きざまに低く踏み込んで足もとを払う」は、引き寄せの2発目の絵 m1025_c_5 を借りていた。
//   c_5 は片方の剣を胸の前で後ろへ構えた払いで、直前の「2本を床へ縦に突き立てた」d_4 から続かない。
//   専用の絵 m1101_delta_sweep_v2 = 低く踏み込み、前の剣を斜め下へ流し切り、後ろの剣を胸の前から後ろへ寝かせた払い (Codex 描き・そざい/blade-own-art-1101/)。
//   1枚目 m1101_delta_sweep (2本をそろえて前へ伸ばした形) は作者が「両手の剣で突いているように見える」で通さなかった (11-01)。
//   v2 は 11-01 作者が pass (見せ直しのページ・メモなし) で配布版の既定。前の c_5 は開発版だけ ?bladeOwnArt=old。
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { SMEAR_ARTS, type ContactSmearArc } from './render'
import { BLADE_SMEAR_BASE, bladeDancerV6Smear, type BladeTrailStyle } from './blade_dancer_art'
import { BLADE_DELTA_ID } from './blade_dancer_delta'
import { BLADE_GAMMA_ID } from './blade_dancer_gamma'
import { BLADE_GRAND_ID, BLADE_QUICK_ID } from './blade_dancer_arle'
import { tidyRecovery } from './blade_dancer_waltz'
import { setBladeDancerPoseFix } from './sprites'

const BOSS_ID = 'blade-dancer'
export const BLADE_DELTA_BORROWED = 'm1025_c_5'
export const BLADE_DELTA_OWN_SWEEP = 'm1101_delta_sweep_v2'

// 払いの跡: 後ろ上 (−30, 58) から体の前 (70, 52) を回って、下の剣先 (122, 12) で終わる弧 (画面 px・足元から前と上)。
//   剣先 = 絵の画素 (4, 882)・足 975・置き場所 927・倍率 1.02・画面 px は絵の画素 × 倍率 × 0.1294。
export const BLADE_DELTA_OWN_SWEEP_SMEAR: ContactSmearArc = {
  ...BLADE_SMEAR_BASE, anchorFwdPx: 12.07, anchorUpPx: -77.1, radiusPx: 141.5, angleFromRad: 1.8727, angleToRad: 0.6812, widthPx: 8,
}

/** δ の払いの絵を専用の絵へ替える (配布版の既定・開発版 ?bladeOwnArt=old で外す)。δ を入れた後 (useBladeDancerArle / useBladeDancerDelta の後) に呼ぶ。 */
export function useBladeOwnArt(trail: BladeTrailStyle = 'afterimage'): void {
  const timeline = BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_DELTA_ID]
  if (!timeline) return
  BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_DELTA_ID] = timeline.map(key =>
    key.image === BLADE_DELTA_BORROWED ? { ...key, image: BLADE_DELTA_OWN_SWEEP } : key)
  Object.assign(SMEAR_ARTS, bladeDancerV6Smear(trail, { [BLADE_DELTA_OWN_SWEEP]: BLADE_DELTA_OWN_SWEEP_SMEAR }))
}

/** 専用の絵を使うか。'old' だけ前の借りた絵 (開発版)。 */
export function parseBladeOwnArt(v: string | null): boolean {
  return v !== 'old'
}

// 11-02 段取りの①: 遅れの一撃 (γ) の戻りの「肩で息をする残心」(11-01 作者 q2「描く」)。
//   γ の元の時間表 (src/blade_dancer_gamma.ts) は旋回突きの引き戻しの絵 m1025_p4_8 を借りていたが、配布版のアルレ版は
//   10-28 夕の戻りの整理 (tidyRecovery) で p4_8 を落とし、振り抜いた g_4 → 両腕を広げる締め w_3 → 構え になっている。
//   専用の絵 m1102_gamma_rest = 振り抜いた低い踏み込みのまま、肩を落としてうなだれ、2本の剣先を床の近くへ下ろした残心 (Codex 描き・そざい/blade-own-art-1102/)。
//   整理の決まり「振り抜き → 息をつく絵1枚 → 締め」(三連・大振りの g3 と同じ置き方) で g_4 と w_3 の間に挟む。
//   11-03 作者の答えで配布版の既定。開発版の ?bladeGammaRest=old だけが呼ばない (記録 docs/research/2026-11-02-blade-next.md)。
export const BLADE_GAMMA_OWN_REST = 'm1102_gamma_rest'

/** γ の戻りに専用の残心を1枚挟む (配布版の既定・アルレ版の後に呼ぶ)。 */
export function useBladeGammaRest(): void {
  const timeline = BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_GAMMA_ID]
  if (!timeline) return
  BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_GAMMA_ID] = tidyRecovery(BLADE_GAMMA_ID, timeline, BLADE_GAMMA_OWN_REST)
}

// 11-03 大振りと速い連の型 (開発版 ?bladeGrandQuick=v1・記録 docs/research/2026-11-03-blade-grand-quick-type.md)。
//   2技とも終舞の絵 m1020_v6_* を借りている。11-01 作者が決めた順 (γ の残心 → 大振り11枚 → 速い連12枚) の前に、技ごとに型1枚を作者に見せる。
//   型 = 当たる瞬間の1枚だけを専用の絵に替える (前後のコマは借りた絵のまま)。通ったら残りのコマを同じ形で描き広げる。
//   大振り: 3発目 g1 (2本をそろえて頭上から振り下ろす) → 前の剣を足首の高さまで振り下ろし切り、後ろの剣を頭上に残して2本を大きく V 字に開いた形。
//   速い連: 1発目 d2 (逆袈裟の斬り上げ) → 前の剣を左下へ鋭く振り下ろし、後ろの剣を上に構えて次を待つ形。
export const BLADE_GRAND_BORROWED = 'm1020_v6_g1'
export const BLADE_GRAND_OWN_TYPE = 'm1103_grand_g1'
export const BLADE_QUICK_BORROWED = 'm1020_v6_d2'
export const BLADE_QUICK_OWN_TYPE = 'm1103_quick_d2'
// 11-02 速い連の合図の構え (作者 q3「専用に描く」)。借りていた w7_c4 は頭の上で X 字＝三連 (A) の終わりと同じ絵で、B が来るのか見分けられない。
//   専用の絵は低く沈んで2本を腰の脇から後ろへ寝かせた構え (頭上の X 字・大振りの頭上の V 字と影絵で被らない)。
export const BLADE_QUICK_SIG_BORROWED = 'm1022_w7_c4'
export const BLADE_QUICK_OWN_SIG = 'm1102_quick_sig'

// 型の1枚の斬りの跡 (画面 px・足元から前と上・中心は前の肩)。どちらも終わりが前の剣先に乗る。
//   速い連: 頭の上の前 (61, 164) から左下の剣先 (103, 32) へ振り下ろす。剣先 = 絵の画素 (18, 760)・足 998・置き場所 773・倍率 1.05。
//   大振り: 頭の真上 (0, 194) から足首の高さの剣先 (96, 4) まで大きく振り下ろす。剣先 = 絵の画素 (18, 976)・足 1005・置き場所 647・倍率 1.18。
export const BLADE_QUICK_OWN_TYPE_SMEAR: ContactSmearArc = {
  ...BLADE_SMEAR_BASE, anchorFwdPx: 28.9, anchorUpPx: 81.3, radiusPx: 88.5, angleFromRad: 1.2, angleToRad: -0.587,
}
export const BLADE_GRAND_OWN_TYPE_SMEAR: ContactSmearArc = {
  ...BLADE_SMEAR_BASE, anchorFwdPx: 14.7, anchorUpPx: 82, radiusPx: 112.4, angleFromRad: 1.7, angleToRad: -0.762, widthPx: 9,
}

// 11-04 v3：配布版の足の運び (つま先で立って細く踏み替える絵) を土台に、借りた絵の崩れだけを直す。
//   11-03 の v2 (残り10枚を足を大きく開いた踏み込みの絵で描き直した版) は作者が「足が広げっぱなしになってすべってるようにしかみえない／前のほうが良かった」で不採用。
//   大振りは1発目の前と2発目・3発目の前で体が前へ流れる (600→490→442→408)。足を開いた絵のまま体を動かすと足が床を滑る。
//   配布版のつま先立ちの細い絵は床に1点で触れるので、流れても滑りに見えない。動く間はこの絵を使い、足を開いた絵は体が止まってから (3発目以降) だけ使う。
//   直す崩れ: 2発目の前後の借りた3枚 (背中を向く e2・剣を真上に立てた当たり e3 と水平に走る跡・後ろ向きの片膝上げ e4)
//   → 同じ終舞の、正面を向いた逆袈裟の斬り上げの3枚 (d1 構え → d2 当たり → d3 振り抜き) に替える。d2 の跡は終舞で剣先に合わせてある。
export const BLADE_GRAND_V3_SECOND: Record<string, string> = { m1020_v6_e2: 'm1020_v6_d1', m1020_v6_e3: 'm1020_v6_d2', m1020_v6_e4: 'm1020_v6_d3' }
//   v3 の締め: 体が止まった後の 3発目の型 g1 (11-02 作者 pass) → 同じ画風で描いた戻り2枚 (g1 から腰が跳ばないよう描かせた・そざい/blade-grand-1103/)。
//   v3b は締めを配布版の借りた絵 (g1・g2・g3) のまま残す。
export const BLADE_GRAND_V3_ENDING = ['g2', 'g3'] as const
export const bladeGrandOwnFrame = (k: string) => `m1103_grand_${k}`

export type BladeGrandQuick = 'off' | 'v1' | 'v3' | 'v3b'

// 11-04 作者の答え (db answers/blade-grand-v3-1104・grand=v3b) で v3b が配布版の既定 (速い連の型 d2 と専用の合図の構えもこれに乗る)。
//   開発版の比較: ?bladeGrandQuick=old で前の借りた絵だけの見た目・=v1 で型だけ・=v3 で締めも専用の絵 (作者は採らなかった)。
/** 開発版の URL の値から版を選ぶ。指定なしは配布版と同じ v3b、'old' だけ前の見た目。 */
export function parseBladeGrandQuick(v: string | null): BladeGrandQuick {
  if (v === 'old') return 'off'
  return v === 'v1' || v === 'v3' ? v : 'v3b'
}

/** 大振りと速い連の当たる絵を型の1枚に替える (配布版は v3b・アルレ版の後に呼ぶ)。終舞そのものの絵は替えない。 */
export function useBladeGrandQuickType(trail: BladeTrailStyle = 'afterimage', version: BladeGrandQuick = 'v1'): void {
  const swap = (id: string, from: string, to: string) => {
    const timeline = BOSS_ATTACK_TIMELINES[BOSS_ID]?.[id]
    if (!timeline) return
    BOSS_ATTACK_TIMELINES[BOSS_ID]![id] = timeline.map(key => key.image === from ? { ...key, image: to } : key)
  }
  if (version !== 'v3b') swap(BLADE_GRAND_ID, BLADE_GRAND_BORROWED, BLADE_GRAND_OWN_TYPE)
  swap(BLADE_QUICK_ID, BLADE_QUICK_BORROWED, BLADE_QUICK_OWN_TYPE)
  swap(BLADE_QUICK_ID, BLADE_QUICK_SIG_BORROWED, BLADE_QUICK_OWN_SIG)
  if (version === 'v3' || version === 'v3b') for (const [from, to] of Object.entries(BLADE_GRAND_V3_SECOND)) swap(BLADE_GRAND_ID, from, to)
  if (version === 'v3') for (const k of BLADE_GRAND_V3_ENDING) swap(BLADE_GRAND_ID, `m1020_v6_${k}`, bladeGrandOwnFrame(k))
  Object.assign(SMEAR_ARTS, bladeDancerV6Smear(trail, {
    [BLADE_GRAND_OWN_TYPE]: BLADE_GRAND_OWN_TYPE_SMEAR, [BLADE_QUICK_OWN_TYPE]: BLADE_QUICK_OWN_TYPE_SMEAR,
  }))
}

// 11-04 速い連の1式を専用の絵に (開発版 ?bladeQuick=v1104 だけ・配布版は上の v3b のまま)。記録 = docs/research/2026-11-04-blade-quick-v1104-ingame.md
//   作者の答え (e3b=ok・d2c=ok) の後に同じ姿で描いた11枚 (m1104_quick_*) で、借りた終舞の絵と型の d2 を全部替える。合図の構え・立ち姿へ戻る絵はそのまま。
//   動き・時間・当たりは替えない (絵の名前だけを替える)。
//   v1104b (11-04 作者 quick=fix「大きさが乱れすぎ」) は絵も時間も同じで、倍率だけを1枚ごとに替えた版。記録 = docs/research/2026-11-04-blade-quick-v1104b-size.md
//   spin1 (11-03 作者 quick=redraw「突きに見える／回転しているようには全く見えない」) は v1104b に、1発目と2発目の間の1回転を足した型。
//     記録 = docs/research/2026-11-03-blade-quick-spin.md
//   spin4 (11-03 作者 spin=yes・next=go) は3つの間すべてに1回転を入れ、4発目を左右へ切り開く h4 に替えた4発通しの版。
//     記録 = docs/research/2026-11-03-blade-quick-spin4.md
//   alt1 (11-03 作者 spin4=「この技は回さないでください」・mix「一番綺麗に作れるかつ他と似すぎていないもの」) は、左右の剣を交互に打ち下ろす形の型。
//     v1104b の2発目だけを、後ろの剣で斬り下ろした直後の1枚に替える。記録 = docs/research/2026-11-04-blade-quick-alternate.md
export type BladeQuickArt = 'off' | 'v1104' | 'v1104b' | 'spin1' | 'spin4' | 'alt1'

export function parseBladeQuickArt(v: string | null): BladeQuickArt {
  return v === 'v1104' || v === 'v1104b' || v === 'spin1' || v === 'spin4' || v === 'alt1' ? v : 'off'
}

const Q = (k: string) => `m1104_quick_${k}`
export const BLADE_QUICK_V1104: Readonly<Record<string, string>> = {
  m1020_v6_d1: Q('d1'), m1020_v6_d2: Q('d2c'), [BLADE_QUICK_OWN_TYPE]: Q('d2c'), m1020_v6_d3: Q('d3'),
  m1020_v6_d4: Q('d4'), m1020_v6_e1: Q('e1'), m1020_v6_e2: Q('e2'), m1020_v6_e3: Q('e3b'),
  m1020_v6_e4: Q('e4'), m1020_v6_f1: Q('f1'), m1020_v6_f2: Q('f2'), m1020_v6_f3: Q('f3'),
}
// 当たる4枚の斬りの跡 (画面 px・足元から前と上)。どれも終わりが新しい絵の前の剣先に乗る (剣先 = 絵の不透明の左端・倍率 0.9)。
//   d2c: 前の肩から左下へ振り下ろし切った剣先 (67, 31)。d4: 左下へ伸びた剣先 (120, 8・画面で合わせ直した)。e3b: 胸の高さで前へ伸ばした剣先 (79, 86) へ後ろから横に薙ぐ。
//   f2: 胸の高さの剣先 (84, 86) へ上から開く。
export const BLADE_QUICK_V1104_SMEAR: Record<string, ContactSmearArc> = {
  [Q('d2c')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 32.7, anchorUpPx: 112, radiusPx: 88.6, angleFromRad: 1.2, angleToRad: -1.17 },
  [Q('d4')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 13.2, anchorUpPx: 98.8, radiusPx: 140, angleFromRad: 1.1, angleToRad: -0.7 },
  [Q('e3b')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: 300, radiusPx: 222.3, angleFromRad: -1.85, angleToRad: -1.3 },
  [Q('f2')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 24.3, anchorUpPx: 102.4, radiusPx: 61.7, angleFromRad: 1.0, angleToRad: -0.27 },
}

// v1104b の倍率。11枚は 1024×1536 に1枚ごとに違う大きさで描かれていた (頭の面と帯の結び目を型合わせで測ると d2c を 1 として 0.70〜1.09・
//   2つの物差しは ±10% 以内で一致)。倍率 = 1.027 ÷ その枚の描かれた大きさ (頭:結び目 = 2:1 の重みの幾何平均) で、画面の頭を約 29.5px
//   (配布版の合図の構え 30.8・立ち姿 30.3 の間) にそろえる。d4 だけは頭と結び目が体の長さに比べて小さく描かれているので、頭で合わせると
//   体が他より 1 割高くなる。体の高さと頭の間を取って 1.2 (頭は約 26px)。
export const BLADE_QUICK_V1104B_FIX: Readonly<Record<string, number>> = {
  [Q('d1')]: 1.093, [Q('d2c')]: 1.029, [Q('d3')]: 1.107, [Q('d4')]: 1.2, [Q('e1')]: 1.049, [Q('e2')]: 1.1,
  [Q('e3b')]: 1.207, [Q('e4')]: 1.465, [Q('f1')]: 1.369, [Q('f2')]: 1.362, [Q('f3')]: 0.975,
}
// 結び目は足元から前へ 87 × 0.12944 = 11.26px (置き場所 = 結び目 + 87 ÷ 倍率・絵の1px = 0.12944 × 倍率 の画面 px)。
//   斬りの跡は結び目と足元を中心に倍率の比で伸ばす (剣先に乗ったまま)。
const QUICK_KNOT_FWD = 87 * 0.12944
function scaleQuickSmear(arc: ContactSmearArc, k: number): ContactSmearArc {
  return { ...arc, anchorFwdPx: QUICK_KNOT_FWD + (arc.anchorFwdPx - QUICK_KNOT_FWD) * k, anchorUpPx: arc.anchorUpPx * k, radiusPx: arc.radiusPx * k }
}
export const BLADE_QUICK_V1104B_SMEAR: Record<string, ContactSmearArc> = Object.fromEntries(
  Object.entries(BLADE_QUICK_V1104_SMEAR).map(([name, arc]) => [name, scaleQuickSmear(arc, BLADE_QUICK_V1104B_FIX[name]! / 0.9)]),
)

// 11-03 回りながら斬る型 (?bladeQuick=spin1)。1発ごとに体が1周する: 横向き (当たり) → 正面 → 反対の横向き → 背中 → 横向き (次の当たり)。
//   型は1発目 (d2c) と2発目の間だけ。1発目の後の間 (164ms) に回る3枚を約55msずつ置き、2発目を前を低く薙ぐ h2 に替える。3・4発目は v1104b のまま。
//   回る3枚はつま先1本で立ち、髪・帯・スカートが回る向きと逆へなびく。拍・時間・当たりは替えない。
const QS = (k: string) => `m1103_qspin_${k}`
export const BLADE_QUICK_SPIN_TURN = [QS('s1'), QS('s2'), QS('s3')] as const
export const BLADE_QUICK_SPIN_HIT2 = QS('h2')
// spin4 の4発目: 頭上から振り下ろした2本を胸の高さで左右へ切り開く。3発目は作者が通した e3b (後ろの剣が上に立つ) をそのまま使う。
export const BLADE_QUICK_SPIN_HIT4 = QS('h4')
// h2 の跡: 後ろ (−55, 41) から体の前を回って剣先 (91, 40) まで膝の高さを横に払う (e3b と同じ上に高い中心の浅い弧)。
export const BLADE_QUICK_SPIN_SMEAR: Record<string, ContactSmearArc> = {
  [BLADE_QUICK_SPIN_HIT2]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: 300, radiusPx: 269.5, angleFromRad: -1.85, angleToRad: -1.304 },
  // h4 の跡: 前の肩を中心に、頭上から胸の高さの剣先まで振り下ろす4分の1の弧 (絵から測った中心の高さ 118.6 は画面で 10px 上に出たので撮って合わせ直した)。
  [BLADE_QUICK_SPIN_HIT4]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 25.5, anchorUpPx: 108.6, radiusPx: 47.4, angleFromRad: 1.75, angleToRad: -0.032 },
}
// alt1 の2発目: 後ろの剣が胸の前を横切って左下へ斬り下ろした直後・前の剣は頭の上へ立てて次を振りかぶる。
export const BLADE_QUICK_ALT_HIT2 = 'm1104_qalt_h2'
export const BLADE_QUICK_ALT_SMEAR: Record<string, ContactSmearArc> = {
  // 跡: 後ろの肩 (14.6, 121.9) を中心に、肩の後ろ上から体の前を横切って左下の剣先 (79.9, 39.3) まで。
  [BLADE_QUICK_ALT_HIT2]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 14.6, anchorUpPx: 121.9, radiusPx: 105.3, angleFromRad: 2.0, angleToRad: -0.902 },
}
// spin1 は1発目の後の間だけ・spin4 は3つの間すべてで回る3枚を使い回す (回る向きが同じなので同じ順)。
function withSpin(timeline: BossAttackTimeline, gaps: number, hits: Readonly<Record<number, string>>): BossAttackTimeline {
  return timeline.flatMap(key => {
    if (key.phase === 'comboGap' && key.hitIndex !== undefined && key.hitIndex < gaps) {
      if (key.tag !== 'followThrough') return []
      return BLADE_QUICK_SPIN_TURN.map((image, i) => ({ ...key, at: i / 3, image, ...(i ? { tag: undefined } : {}) }))
    }
    const hit = key.phase === 'active' && key.tag === 'contact' && key.hitIndex !== undefined ? hits[key.hitIndex] : undefined
    return [hit ? { ...key, image: hit } : key]
  })
}

/** 速い連の絵だけを新しい11枚へ替える (大振りの後に呼ぶ)。大振り・終舞の絵は替えない。v1104b は倍率も1枚ごとに替える。 */
export function useBladeQuickArt(trail: BladeTrailStyle = 'afterimage', version: Exclude<BladeQuickArt, 'off'> = 'v1104'): void {
  const timeline = BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_QUICK_ID]
  if (!timeline) return
  BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_QUICK_ID] = timeline.map(key => {
    const to = key.image ? BLADE_QUICK_V1104[key.image] : undefined
    return to ? { ...key, image: to } : key
  })
  if (version !== 'v1104') setBladeDancerPoseFix(BLADE_QUICK_V1104B_FIX, 87)
  if (version === 'spin1') BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_QUICK_ID] = withSpin(BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_QUICK_ID]!, 1, { 1: BLADE_QUICK_SPIN_HIT2 })
  if (version === 'alt1') BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_QUICK_ID] = withSpin(BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_QUICK_ID]!, 0, { 1: BLADE_QUICK_ALT_HIT2 })
  if (version === 'spin4') {
    BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_QUICK_ID] = withSpin(BOSS_ATTACK_TIMELINES[BOSS_ID]![BLADE_QUICK_ID]!, 3, { 1: BLADE_QUICK_SPIN_HIT2, 3: BLADE_QUICK_SPIN_HIT4 })
  }
  Object.assign(SMEAR_ARTS, bladeDancerV6Smear(trail, version === 'v1104' ? BLADE_QUICK_V1104_SMEAR
    : { ...BLADE_QUICK_V1104B_SMEAR, ...(version === 'spin1' || version === 'spin4' ? BLADE_QUICK_SPIN_SMEAR : {}), ...(version === 'alt1' ? BLADE_QUICK_ALT_SMEAR : {}) }))
}
